import { useQuery } from '@tanstack/react-query';
import { Ban, CheckCircle, Clock, Flame, Globe } from 'lucide-react';
import React from 'react';

import {
  getDivisionIcon,
  getTransparentDivisionIcon,
} from '@/components/deprecated/DivisionIcon';
import { StatusBoxGradient } from '@/components/deprecated/StatusBoxGradient';
import { Skeleton } from '@/components/ui/skeleton';
import { convertNumberToDivision } from '@/utils/numberToDivision';

import { fetchTeamStatusBox } from '../api/getTeamStatusBox';
import { TeamLeagueState } from '../types';

interface TeamStatusBoxProps {
  teamId: string;
}

const TeamStatusBox: React.FC<TeamStatusBoxProps> = ({ teamId }) => {
  const { data: teamLeagueStatus, isLoading: teamLeagueStatusLoading } =
    useQuery({
      queryKey: ['teamLeagueStatus', teamId],
      queryFn: () => fetchTeamStatusBox(teamId),
    });

  if (teamLeagueStatusLoading) {
    return (
      <div className="border-block relative flex w-full flex-col items-center overflow-hidden rounded-md border p-10">
        <StatusBoxGradient />
        <div className="flex items-center gap-x-2 text-primary">
          <Skeleton className="h-5 w-5" />
          <Skeleton className="h-2.5 w-32" />
        </div>
        <Skeleton className="mb-12 mt-6 h-20 w-48" />
        <Skeleton className="h-2.5 w-32" />
      </div>
    );
  }

  if (!teamLeagueStatus) {
    return (
      <div className="border-block relative flex items-center gap-x-2 overflow-hidden rounded-md border p-10 text-sm text-muted-foreground">
        <StatusBoxGradient />
        <Ban />
        Something went wrong...
      </div>
    );
  }

  if (teamLeagueStatus.state === TeamLeagueState.InActiveLeagueButNotPaid) {
    return (
      <div className="border-block relative flex w-full flex-col items-center overflow-hidden rounded-md border p-10">
        <StatusBoxGradient />
        <div className="mb-2">
          {getTransparentDivisionIcon(teamLeagueStatus.division, '90')}
        </div>
        <div className="mb-16 text-2xl font-medium">
          {convertNumberToDivision(teamLeagueStatus.division)} Division
        </div>
        <div className="flex items-center gap-x-2 text-xs font-medium text-[#FECC00]">
          <Clock />
          Awaiting payment for Season {teamLeagueStatus.season}
        </div>
      </div>
    );
  }

  if (teamLeagueStatus.state === TeamLeagueState.InActiveLeagueAndPaid) {
    return (
      <div className="border-block relative flex w-full flex-col items-center overflow-hidden rounded-md border p-10">
        <StatusBoxGradient />
        <div className="mb-2">
          {getTransparentDivisionIcon(teamLeagueStatus.division, '90')}
        </div>
        <div className="mb-16 text-2xl font-medium">
          {convertNumberToDivision(teamLeagueStatus.division)} Division
        </div>
        <div className="flex items-center gap-x-2 text-xs font-medium text-[#0BEA21]">
          <CheckCircle />
          Signed up for Season {teamLeagueStatus.season}
        </div>
      </div>
    );
  }

  if (teamLeagueStatus.state === TeamLeagueState.InRegularSeason) {
    return (
      <div className="border-block relative flex w-full flex-col items-center overflow-hidden rounded-md border p-10">
        <StatusBoxGradient />
        <div className="flex items-center gap-x-2 text-primary">
          {getDivisionIcon(teamLeagueStatus?.division)}
          <span className="font-medium ">
            {convertNumberToDivision(teamLeagueStatus?.division)} Division
          </span>
        </div>
        <div className="mb-6 text-[100px] font-medium">
          {teamLeagueStatus?.standings?.stand_win}-
          {teamLeagueStatus?.standings?.stand_lose}
        </div>
        <div className="flex items-center gap-x-2 text-sm font-medium text-primary">
          <Globe />
          Regular Season {teamLeagueStatus.season}
        </div>
      </div>
    );
  }

  if (teamLeagueStatus.state === TeamLeagueState.InPlayoffs) {
    return (
      <div className="border-block relative flex w-full flex-col items-center overflow-hidden rounded-md border p-10">
        <StatusBoxGradient />
        <div className="flex items-center gap-x-2 text-primary">
          {getDivisionIcon(teamLeagueStatus?.division)}
          <span className="font-medium ">
            {convertNumberToDivision(teamLeagueStatus?.division)} Division
          </span>
        </div>
        <div className="mb-6 text-[100px] font-medium">
          {teamLeagueStatus?.standings?.stand_win}-
          {teamLeagueStatus?.standings?.stand_lose}
        </div>
        <div className="flex items-center gap-x-2 text-sm font-medium text-primary">
          <Flame />
          Playoffs Season {teamLeagueStatus.season}
        </div>
      </div>
    );
  }

  if (teamLeagueStatus.state === TeamLeagueState.NotInActiveLeague) {
    return (
      <div className="border-block relative flex items-center gap-x-2 overflow-hidden rounded-md border p-10 text-sm text-muted-foreground">
        <StatusBoxGradient />
        <Ban />
        Not currently in an active league
      </div>
    );
  }

  return (
    <div className="border-block relative flex items-center gap-x-2 overflow-hidden rounded-md border p-10 text-sm text-muted-foreground">
      <StatusBoxGradient />
      <Ban />
      Something went wrong...
    </div>
  );
};

export default TeamStatusBox;
